import {Howl} from "howler";
import * as PIXI from "pixi.js";
import {config} from "../config";
import {SessionTracker} from "../SessionTracker";
import {ApiService} from "./api/ApiService";
import {BlackjackAction} from "./api/BlackjackAction";
import {BlackjackActionName} from "./api/BlackjackActionName";
import {BlackjackHand} from "./api/serverResponseClasses/BlackjackHand";
import {BlackjackInfoForClient} from "./api/serverResponseClasses/BlackjackInfoForClient";
import {ServerResponse} from "./api/serverResponseClasses/ServerResponse";
import {BetsAreasPanel} from "./BetsAreasPanel";
import {BlackjackHandsSimulation} from "./BlackjackHandsSimulation";
import {Card} from "./Card";
import data from "./configs/data";
import {FunModeFeatures} from "./FunModeFeatures/FunModeFeatures";
import {HandPosition} from "./HandPosition";
import {HowToPlay} from "./HowToPlay";
import {InfoModal} from "./InfoModal";
import {InfoTextField} from "./InfoTextField";
import {PixiApplication} from "./PixiApplication";
import {ActionsButtonsPanel} from "./userInterface/ActionsButtonsPanel";
import {BetPicker} from "./userInterface/betPicker/BetPicker";
import {IBetPicker} from "./userInterface/betPicker/IBetPicker";
import {Info} from "./userInterface/Info";
import {OptionsPanel} from "./userInterface/OptionsPanel";
import {Utils} from "./Utils";

export class Blackjack extends PIXI.Container {

    private readonly ACTIONS_BUTTONS_PANEL_POSITION = new PIXI.Point(PixiApplication.WIDTH / 2, 650);
    private readonly BET_PICKER_POSITION = new PIXI.Point(1071, 651);
    private readonly HOW_TO_PLAY_POSITION = new PIXI.Point(0, 0);
    private readonly OPTIONS_PANEL_POSITION = new PIXI.Point(50, 527);

    private readonly BET_VALUE_FIELD_POSITION = new PIXI.Point(242, 610);
    private readonly REWARD_TEXT_FIELD_POSITION = new PIXI.Point(242, 655);
    private readonly BALANCE_TEXT_FIELD_POSITION = new PIXI.Point(242, 700);

    private steemAccountName: string;
    private steemConnectToken: string;
    private keychainJWT: string;
    private betPicker: IBetPicker;
    private betsAreasPanel: BetsAreasPanel;
    private actionsButtonsPanel: ActionsButtonsPanel;
    private blackjackHandsSimulation: BlackjackHandsSimulation;

    private apiService = new ApiService();
    private sessionTracker: SessionTracker;
    private connectionTimeOut: any;
    private showSlowConnectionModalAgain = true;
    private previousBlackjackInfoForClient: BlackjackInfoForClient;

    private winValueField: InfoTextField;
    private betValueField: InfoTextField;
    private balanceField: InfoTextField;
    private steemBalance: number;

    private cardsDealingSound: Howl;
    private nextCardSound: Howl;

    constructor() {
        super();

        this.createAndAddBackground();
        this.createAndAddBetAreasPanel();
        this.createAndAddCardsAndChipsStacksSimulation();
        this.createAndAddBetPicker();
        this.createAndAddActionsPanel();
        this.createAndAddBetTextField();
        this.createAndAddWinTextField();
        this.createAndAddBalanceTextField();
        this.createAndAddOptionsPanel();
        this.createAndAddHowToPlay();
        this.createAndAddInfo();

        this.betsAreasPanel.disable();
        this.actionsButtonsPanel.hideAllButtons();

        this.cardsDealingSound = new Howl({
            src: [data.audios.cardsDealing],
            volume: 0.7,
        });

        this.nextCardSound = new Howl({
            src: [data.audios.nextCard],
            volume: 0.7,
        });
    }

    public setCredentials(steemAccountName: string, steemConnectToken: string, keychainJWT: string): void {
        this.steemAccountName = steemAccountName;
        this.steemConnectToken = steemConnectToken;
        this.keychainJWT = keychainJWT;
    }

    public async initGame(): Promise<any> {
        const serverResponse = (await this.apiService.initGame(
            this.steemAccountName, this.steemConnectToken, this.keychainJWT));
        this.sessionTracker = new SessionTracker(serverResponse.sessionParam);
        this.balanceField.setValue(
            parseFloat(serverResponse.accountInfo.steemAmount.toFixed(6)).toString(), config.baseCurrency);
        this.steemBalance = serverResponse.accountInfo.steemAmount;
        this.betValueField.setValue(0 + "", config.baseCurrency);
        this.winValueField.setValue(0 + "", config.baseCurrency);
        this.showNextAvailableActions(serverResponse.blackjackInfoForClient);
        if (serverResponse.blackjackInfoForClient) {
            this.betValueField.setValue(
                serverResponse.blackjackInfoForClient.totalBet.toFixed(Utils.getBetPrecision()), config.baseCurrency);
            this.winValueField.setValue(
                serverResponse.blackjackInfoForClient.totalWin.toFixed(Utils.getBetPrecision()), config.baseCurrency);
        }
        this.showNextAvailableActions(serverResponse.blackjackInfoForClient);
        this.previousBlackjackInfoForClient = serverResponse.blackjackInfoForClient;
        this.actionsButtonsPanel.disable();
        this.betsAreasPanel.enable();

        if (serverResponse.blackjackInfoForClient) {
            this.initializeBlackjackHands(serverResponse.blackjackInfoForClient);
            this.actionsButtonsPanel.enable();
            this.betsAreasPanel.disable();
        }
    }

    private async setGameState(blackjackAction: BlackjackAction): Promise<void> {

        this.actionsButtonsPanel.hideAllButtons();
        this.betsAreasPanel.disable();

        if (this.showSlowConnectionModalAgain) {
            this.startSlowConnectionCountDownAndInitializeBehavior();
        }

        const serverResponse: ServerResponse = await this.apiService.setGameState(blackjackAction, config.baseCurrency);
        if (this.showSlowConnectionModalAgain) {
            this.removeConnectionCountDown();
        }

        this.sessionTracker.reloadIfSessionParamMismatch(serverResponse.sessionParam);
        this.balanceField.setValue(
            parseFloat(serverResponse.accountInfo.steemAmount.toFixed(6)).toString(), config.baseCurrency);
        this.steemBalance = serverResponse.accountInfo.steemAmount;
        this.betValueField.setValue(
            parseFloat(serverResponse.blackjackInfoForClient.totalBet.toFixed(6)).toString(), config.baseCurrency);
        this.winValueField.setValue(
            parseFloat(serverResponse.blackjackInfoForClient.totalWin.toFixed(6)).toString(), config.baseCurrency);

        await this.animateActionPerformed(serverResponse.blackjackInfoForClient, blackjackAction.getActionName());

        await this.brailCardsIfHandChangedAndNotDealAction(serverResponse.blackjackInfoForClient, blackjackAction);

        await this.moveHighlighterToCurrentHand(serverResponse.blackjackInfoForClient);
        if (this.previousBlackjackInfoForClient &&
            !this.previousBlackjackInfoForClient.insurancePhaseFinished &&
            serverResponse.blackjackInfoForClient.insurancePhaseFinished) {
            await this.blackjackHandsSimulation.checkDealerForBlackjack();
        }

        if (this.previousBlackjackInfoForClient &&
            !this.previousBlackjackInfoForClient.insurancePhaseFinished &&
            serverResponse.blackjackInfoForClient.insurancePhaseFinished &&
            !serverResponse.blackjackInfoForClient.playerActionsFinished) {
            await this.blackjackHandsSimulation.removeInsuranceChipsStacks();
        }

        if (serverResponse.blackjackInfoForClient.playerActionsFinished) {
            const allHandsBust = this.doesAllHandsBust(serverResponse.blackjackInfoForClient);
            await this.animateDealerCardsDrawing(serverResponse.blackjackInfoForClient, allHandsBust);
            await this.blackjackHandsSimulation.setWinningChipsStacks();
            await this.blackjackHandsSimulation.setWinInfoLabels();
        }

        this.showNextAvailableActions(serverResponse.blackjackInfoForClient);

        FunModeFeatures.addSpin();
        this.previousBlackjackInfoForClient = serverResponse.blackjackInfoForClient;
    }

    private async brailCardsIfHandChangedAndNotDealAction(blackjackInfoForClient: BlackjackInfoForClient,
                                                          blackjackAction: BlackjackAction): Promise<void> {
        if (this.previousBlackjackInfoForClient &&
            blackjackAction.getActionName() !== BlackjackActionName.DEAL &&
            this.previousBlackjackInfoForClient.currentHandIndex !== blackjackInfoForClient.currentHandIndex) {

            const handFromGameState: BlackjackHand =
                this.previousBlackjackInfoForClient.blackjackHands[
                    this.previousBlackjackInfoForClient.currentHandIndex];

            await this.blackjackHandsSimulation.brailCards(
                handFromGameState.getHandPosition(),
                handFromGameState.getSplitOnePosition(), handFromGameState.getSplitTwoPosition());
        }
    }

    private async moveHighlighterToCurrentHand(blackjackInfoForClient: BlackjackInfoForClient): Promise<void> {
        const currentHand = blackjackInfoForClient.blackjackHands[blackjackInfoForClient.currentHandIndex];
        const currentInsureHand = blackjackInfoForClient.blackjackHands[
            blackjackInfoForClient.currentInsuranceHandIndex];
        this.blackjackHandsSimulation.hideHighlighterArrow();
        if (blackjackInfoForClient.playerActionsFinished) {
            await this.blackjackHandsSimulation.moveHighlighter(HandPosition.DEALER);
        } else if (blackjackInfoForClient.insurancePhaseFinished) {
            await this.blackjackHandsSimulation.moveHighlighter(currentHand.getHandPosition(),
                currentHand.getSplitOnePosition(), currentHand.getSplitTwoPosition());
            this.blackjackHandsSimulation.showHighlighterArrow();
        } else if (!blackjackInfoForClient.insurancePhaseFinished) {
            await this.blackjackHandsSimulation.moveHighlighter(currentInsureHand.getHandPosition(),
                currentInsureHand.getSplitOnePosition(), currentInsureHand.getSplitTwoPosition());
            this.blackjackHandsSimulation.showHighlighterArrow();
        }
    }

    private async animateDealerCardsDrawing(blackjackInfoForClient: BlackjackInfoForClient,
                                            allHandsBust: boolean): Promise<void> {
        const dealerHand = blackjackInfoForClient.dealerHand;
        for (let i = 1; i < dealerHand.getCards().length; i++) {
            if (allHandsBust && i === 2) {
                break;
            }
            if (i !== 1) {
                await Utils.requestTimeout(400);
            }
            this.nextCardSound.play();
            await this.blackjackHandsSimulation.addCard(
                dealerHand.getCards()[i], HandPosition.DEALER);
        }
    }

    private doesAllHandsBust(blackjackInfoForClient: BlackjackInfoForClient): boolean {
        const allPlayerHands = blackjackInfoForClient.blackjackHands;
        for (let i = 0; i < allPlayerHands.length; i++) {
            if (!allPlayerHands[i].doesBust()) {
                return false;
            }
        }
        return true;
    }

    private initializeBlackjackHands(blackjackInfoForClient: BlackjackInfoForClient): void {
        let handFromGameState: BlackjackHand;
        this.blackjackHandsSimulation.addCardWithoutAnimation(blackjackInfoForClient.dealerHand.getCards()[0],
            HandPosition.DEALER);
        this.blackjackHandsSimulation.setBetAmount(blackjackInfoForClient.dealerHand.getBetAmount(),
            HandPosition.DEALER);

        const currentHand = blackjackInfoForClient.blackjackHands[blackjackInfoForClient.currentHandIndex];
        this.blackjackHandsSimulation.highlight(currentHand.getHandPosition(),
            currentHand.getSplitOnePosition(), currentHand.getSplitTwoPosition());

        this.blackjackHandsSimulation.drawDealerCoveredCard(false);

        for (let i = 0; i < blackjackInfoForClient.blackjackHands.length; i++) {
            handFromGameState = blackjackInfoForClient.blackjackHands[i];
            for (let j = 0; j < handFromGameState.getCards().length; j++) {
                this.blackjackHandsSimulation.addCardWithoutAnimation(
                    handFromGameState.getCards()[j], handFromGameState.getHandPosition(),
                    handFromGameState.getSplitOnePosition(), handFromGameState.getSplitTwoPosition());
            }
            this.blackjackHandsSimulation.setBetAmount(
                handFromGameState.getBetAmount(), handFromGameState.getHandPosition(),
                handFromGameState.getSplitOnePosition(), handFromGameState.getSplitTwoPosition());

            if (!blackjackInfoForClient.insurancePhaseFinished && handFromGameState.isHandInsured()) {
                this.blackjackHandsSimulation.insure(handFromGameState.getHandPosition());
            }
            if (handFromGameState.isDoubled()) {
                this.blackjackHandsSimulation.doubleHand(handFromGameState.getHandPosition());
            }
            if (i < blackjackInfoForClient.currentHandIndex) {
                this.blackjackHandsSimulation.brailCards(
                    handFromGameState.getHandPosition(),
                    handFromGameState.getSplitOnePosition(), handFromGameState.getSplitTwoPosition());
            }
        }
    }

    private async animateActionPerformed(blackjackInfoForClient: BlackjackInfoForClient,
                                         blackjackActionName: BlackjackActionName): Promise<void> {
        switch (blackjackActionName) {
            case BlackjackActionName.DEAL: {
                await this.animateDealAction(blackjackInfoForClient);
                break;
            }
            case BlackjackActionName.DOUBLE: {
                await this.animateDoubleAction(blackjackInfoForClient);
                break;
            }
            case BlackjackActionName.HIT: {
                await this.animateHitAction(blackjackInfoForClient);
                break;
            }
            case BlackjackActionName.STAND: {
                return;
            }
            case BlackjackActionName.SPLIT: {
                await this.animateSplitAction(blackjackInfoForClient);
                break;
            }
            case BlackjackActionName.INSURE: {
                await this.animateInsureAction(blackjackInfoForClient);
                break;
            }
            default:
                throw new Error("Illegal action proposed!");
        }
    }

    private async animateDealAction(blackjackInfoForClient: BlackjackInfoForClient): Promise<void> {
        let card: Card, bjHand: BlackjackHand;
        const dealerCard = blackjackInfoForClient.dealerHand.getCards()[0];
        this.cardsDealingSound.play();
        await this.blackjackHandsSimulation.addCard(
            dealerCard, HandPosition.DEALER);
        this.cardsDealingSound.play();
        await this.blackjackHandsSimulation.drawDealerCoveredCard();
        for (let i = 0; i < blackjackInfoForClient.blackjackHands.length; i++) {
            bjHand = blackjackInfoForClient.blackjackHands[i];
            for (let j = 0; j < bjHand.getCards().length; j++) {
                card = bjHand.getCards()[j];
                this.cardsDealingSound.play();
                await this.blackjackHandsSimulation.addCard(
                    card, bjHand.getHandPosition(), bjHand.getSplitOnePosition(), bjHand.getSplitTwoPosition());
            }
        }
        if (dealerCard.getValue() === 10) {
            await this.blackjackHandsSimulation.checkDealerForBlackjack();
        }
    }

    private async animateDoubleAction(blackjackInfoForClient: BlackjackInfoForClient): Promise<void> {
        const previousBjHand: BlackjackHand = blackjackInfoForClient.blackjackHands[
            this.previousBlackjackInfoForClient.currentHandIndex];
        this.nextCardSound.play();
        await this.blackjackHandsSimulation.addCard(
            previousBjHand.getCards()[2], previousBjHand.getHandPosition());
        this.blackjackHandsSimulation.doubleHand(previousBjHand.getHandPosition());
    }

    private async animateHitAction(blackjackInfoForClient: BlackjackInfoForClient): Promise<void> {
        const bjHand = blackjackInfoForClient.blackjackHands[this.previousBlackjackInfoForClient.currentHandIndex];
        const card = bjHand.getCards()[bjHand.getCards().length - 1];
        this.nextCardSound.play();
        await this.blackjackHandsSimulation.addCard(card,
            bjHand.getHandPosition(), bjHand.getSplitOnePosition(), bjHand.getSplitTwoPosition());
    }

    private async animateSplitAction(blackjackInfoForClient: BlackjackInfoForClient): Promise<void> {
        this.blackjackHandsSimulation.hideHighlighter();
        const bjHandToSplit =
            this.previousBlackjackInfoForClient.blackjackHands[this.previousBlackjackInfoForClient.currentHandIndex];
        await this.blackjackHandsSimulation.split(bjHandToSplit.getHandPosition(), bjHandToSplit.getSplitOnePosition());
        const newBjHand1 = blackjackInfoForClient.blackjackHands[this.previousBlackjackInfoForClient.currentHandIndex];
        this.nextCardSound.play();
        await this.blackjackHandsSimulation.addCard(newBjHand1.getCards()[1],
            newBjHand1.getHandPosition(), newBjHand1.getSplitOnePosition(), newBjHand1.getSplitTwoPosition());
        const newBjHand2 = blackjackInfoForClient.blackjackHands[
        this.previousBlackjackInfoForClient.currentHandIndex + 1];
        this.nextCardSound.play();
        await this.blackjackHandsSimulation.addCard(newBjHand2.getCards()[1],
            newBjHand2.getHandPosition(), newBjHand2.getSplitOnePosition(), newBjHand2.getSplitTwoPosition());
    }

    private async animateInsureAction(blackjackInfoForClient: BlackjackInfoForClient): Promise<void> {
        const bjHand = blackjackInfoForClient.blackjackHands[
            this.previousBlackjackInfoForClient.currentInsuranceHandIndex];
        if (bjHand.isHandInsured()) {
            await this.blackjackHandsSimulation.insure(bjHand.getHandPosition());
        }
    }

    private showNextAvailableActions(blackjackInfoForClient: BlackjackInfoForClient): void {
        if (!blackjackInfoForClient) {
            this.actionsButtonsPanel.showAvailableActions([BlackjackActionName.DEAL]);
            return;
        }
        if (!blackjackInfoForClient.insurancePhaseFinished) {
            this.actionsButtonsPanel.showAvailableActions([BlackjackActionName.INSURE]);
            return;
        }

        if (blackjackInfoForClient.playerActionsFinished) {
            this.actionsButtonsPanel.showReBetAndChangeBetButtons();
            return;
        }

        const availableActions: Array<BlackjackActionName> = [BlackjackActionName.HIT, BlackjackActionName.STAND];
        const currentHand = blackjackInfoForClient.blackjackHands[blackjackInfoForClient.currentHandIndex];
        if (currentHand.getCards().length === 2 &&
            currentHand.getSplitNumber() === 0) {
            availableActions.push(BlackjackActionName.DOUBLE);
        }
        if (currentHand.getCards().length === 2 &&
            currentHand.getSplitNumber() < 2 &&
            currentHand.getCards()[0].getValue() === currentHand.getCards()[1].getValue()) {
            availableActions.push(BlackjackActionName.SPLIT);
        }
        this.actionsButtonsPanel.showAvailableActions(availableActions);
    }

    private startSlowConnectionCountDownAndInitializeBehavior(): void {
        this.connectionTimeOut = setTimeout(() => {
            InfoModal.open("Internet connection problems.",
                "You lost you internet connection or your connection is slow.", () => {
                    this.showSlowConnectionModalAgain = false;
                }, true);
            InfoModal.setActionButtonText("Don't show again.");
        }, 5000);
    }

    private removeConnectionCountDown(): void {
        clearTimeout(this.connectionTimeOut);
    }

    private createAndAddActionsPanel(): void {
        this.actionsButtonsPanel = new ActionsButtonsPanel();
        this.actionsButtonsPanel.position = this.ACTIONS_BUTTONS_PANEL_POSITION;
        this.actionsButtonsPanel.addListenerOnActionChosen((blackjackAction: BlackjackAction) => {
            if (blackjackAction.getActionName() === BlackjackActionName.DEAL) {
                blackjackAction.setBets(this.blackjackHandsSimulation.getBlackJackBets());
                if (blackjackAction.getBetAmount() > this.steemBalance) {
                    InfoModal.open("Not enough Hive to perform this action",
                        "You need more Hive to perform this action", () => {
                        }, true);
                    InfoModal.setActionButtonText("OK");
                    return;
                }
            }

            if (blackjackAction.getActionName() === BlackjackActionName.SPLIT) {
                const hand = this.previousBlackjackInfoForClient.blackjackHands[
                    this.previousBlackjackInfoForClient.currentHandIndex];
                if (hand.getBetAmount() > this.steemBalance) {
                    InfoModal.open("Not enough Hive to perform this action",
                        "You need more Hive to perform this action", () => {
                        }, true);
                    InfoModal.setActionButtonText("OK");
                    return;
                }
            }

            if (blackjackAction.getActionName() === BlackjackActionName.INSURE && blackjackAction.getInsureDecision()) {
                const hand = this.previousBlackjackInfoForClient.blackjackHands[
                    this.previousBlackjackInfoForClient.currentInsuranceHandIndex];
                if ((hand.getBetAmount() / 2) > this.steemBalance) {
                    InfoModal.open("Not enough Hive to perform this action",
                        "You need more Hive to perform this action", () => {
                        }, true);
                    InfoModal.setActionButtonText("OK");
                    return;
                }
            }

            if (blackjackAction.getActionName() === BlackjackActionName.DOUBLE) {
                const hand = this.previousBlackjackInfoForClient.blackjackHands[
                    this.previousBlackjackInfoForClient.currentHandIndex];
                if (hand.getBetAmount() > this.steemBalance) {
                    InfoModal.open("Not enough Hive to perform this action",
                        "You need more Hive to perform this action", () => {
                        }, true);
                    InfoModal.setActionButtonText("OK");
                    return;
                }
            }
            this.setGameState(blackjackAction);
        });

        this.actionsButtonsPanel.addListenerOnClearBetClicked(() => {
            this.blackjackHandsSimulation.removeHandBet(HandPosition.PLAYER_LEFT);
            this.blackjackHandsSimulation.removeHandBet(HandPosition.PLAYER_CENTER);
            this.blackjackHandsSimulation.removeHandBet(HandPosition.PLAYER_RIGHT);
            this.actionsButtonsPanel.disable();
            this.betValueField.setValue(this.getTotalBetAmount().toFixed(Utils.getBetPrecision()), config.baseCurrency);
        });
        this.actionsButtonsPanel.addOnChangeBetClickedListener(async () => {
            this.actionsButtonsPanel.hideAllButtons();
            await this.blackjackHandsSimulation.clear();
            this.winValueField.setValue("" + 0, config.baseCurrency);
            this.setBetsFromPreviousGame();
            this.betsAreasPanel.enable();
            this.actionsButtonsPanel.showAvailableActions([BlackjackActionName.DEAL]);
        });
        this.actionsButtonsPanel.addOnReBetClickedListener(() => {
            this.actionsButtonsPanel.hideAllButtons();
            this.blackjackHandsSimulation.clear();
            this.setBetsFromPreviousGame();
            const blackjackAction = new BlackjackAction(BlackjackActionName.DEAL);
            blackjackAction.setBets(this.blackjackHandsSimulation.getBlackJackBets());
            if (blackjackAction.getBetAmount() > this.steemBalance) {
                InfoModal.open("Not enough Hive to perform this action",
                    "You need more Hive to perform this action", () => {
                    }, true);
                InfoModal.setActionButtonText("OK");
                return;
            }
            this.setGameState(blackjackAction);
        });
        this.addChild(this.actionsButtonsPanel);
    }

    private setBetsFromPreviousGame(): void {
        let bjHand;
        bjHand = this.previousBlackjackInfoForClient.getFirstHandWithPosition(HandPosition.PLAYER_LEFT);
        if (bjHand) {
            this.blackjackHandsSimulation.setBetAmount(
                bjHand.getBetAmount(),
                HandPosition.PLAYER_LEFT);
        }
        bjHand = this.previousBlackjackInfoForClient.getFirstHandWithPosition(HandPosition.PLAYER_CENTER);
        if (bjHand) {
            this.blackjackHandsSimulation.setBetAmount(
                bjHand.getBetAmount(),
                HandPosition.PLAYER_CENTER);
        }
        bjHand = this.previousBlackjackInfoForClient.getFirstHandWithPosition(HandPosition.PLAYER_RIGHT);
        if (bjHand) {
            this.blackjackHandsSimulation.setBetAmount(
                bjHand.getBetAmount(),
                HandPosition.PLAYER_RIGHT);
        }
    }

    private createAndAddBetAreasPanel(): void {
        this.betsAreasPanel = new BetsAreasPanel();
        this.betsAreasPanel.addOnLeftAreaClickedListener(() => {
            const betPickerValue = this.betPicker.getBetValue();
            const betAmount = this.blackjackHandsSimulation.getBlackJackBet(HandPosition.PLAYER_LEFT).getBetAmount();
            if (betPickerValue > 0) {
                this.blackjackHandsSimulation.getBlackJackBets();
                if ((betAmount + betPickerValue) > config.maxBet) {
                    this.blackjackHandsSimulation.increaseBetAmount(config.maxBet - betAmount,
                        HandPosition.PLAYER_LEFT);
                } else {
                    this.blackjackHandsSimulation.increaseBetAmount(betPickerValue, HandPosition.PLAYER_LEFT);
                }
                this.actionsButtonsPanel.enable();
            } else {
                this.blackjackHandsSimulation.removeHandBet(HandPosition.PLAYER_LEFT);
                this.disablePanelIfBetIsZero();
            }
            this.betValueField.setValue(this.getTotalBetAmount().toFixed(Utils.getBetPrecision()), config.baseCurrency);
        });
        this.betsAreasPanel.addOnCenterAreaClickedListener(() => {
            const betPickerValue = this.betPicker.getBetValue();
            const betAmount = this.blackjackHandsSimulation.getBlackJackBet(HandPosition.PLAYER_CENTER).getBetAmount();
            if (betPickerValue > 0) {
                if ((betAmount + betPickerValue) > config.maxBet) {
                    this.blackjackHandsSimulation.increaseBetAmount(config.maxBet - betAmount,
                        HandPosition.PLAYER_CENTER);
                } else {
                    this.blackjackHandsSimulation.increaseBetAmount(betPickerValue, HandPosition.PLAYER_CENTER);
                }
                this.actionsButtonsPanel.enable();
            } else {
                this.blackjackHandsSimulation.removeHandBet(HandPosition.PLAYER_CENTER);
                this.disablePanelIfBetIsZero();
            }
            this.betValueField.setValue(this.getTotalBetAmount().toFixed(Utils.getBetPrecision()), config.baseCurrency);
        });
        this.betsAreasPanel.addOnRightAreaClickedListener(() => {
            const betPickerValue = this.betPicker.getBetValue();
            const betAmount = this.blackjackHandsSimulation.getBlackJackBet(HandPosition.PLAYER_RIGHT).getBetAmount();
            if (betPickerValue > 0) {
                if ((betAmount + betPickerValue) > config.maxBet) {
                    this.blackjackHandsSimulation.increaseBetAmount(config.maxBet - betAmount,
                        HandPosition.PLAYER_RIGHT);
                } else {
                    this.blackjackHandsSimulation.increaseBetAmount(betPickerValue, HandPosition.PLAYER_RIGHT);
                }
                this.actionsButtonsPanel.enable();
            } else {
                this.blackjackHandsSimulation.removeHandBet(HandPosition.PLAYER_RIGHT);
                this.disablePanelIfBetIsZero();
            }
            this.betValueField.setValue(this.getTotalBetAmount().toFixed(Utils.getBetPrecision()), config.baseCurrency);
        });
        this.addChild(this.betsAreasPanel);
    }

    private disablePanelIfBetIsZero(): void {
        if (this.getTotalBetAmount() === 0) {
            this.actionsButtonsPanel.disable();
        }
    }

    private getTotalBetAmount(): number {
        const blackjackBets = this.blackjackHandsSimulation.getBlackJackBets();
        let betAmount = 0;
        for (let i = 0; i < blackjackBets.length; i++) {
            betAmount += blackjackBets[i].getBetAmount();
        }
        return betAmount;
    }

    private createAndAddBetTextField(): void {
        this.betValueField = new InfoTextField("BET:");
        this.betValueField.position = this.BET_VALUE_FIELD_POSITION;
        this.betValueField.setValue("0.00", config.baseCurrency);
        this.addChild(this.betValueField);
    }

    private createAndAddWinTextField(): void {
        this.winValueField = new InfoTextField("WIN:");
        this.winValueField.setValue("", config.baseCurrency);
        this.winValueField.position = this.REWARD_TEXT_FIELD_POSITION;
        this.addChild(this.winValueField);
    }

    private createAndAddBalanceTextField(): void {
        this.balanceField = new InfoTextField("");
        this.balanceField.position = this.BALANCE_TEXT_FIELD_POSITION;
        this.addChild(this.balanceField);
    }

    private createAndAddCardsAndChipsStacksSimulation(): void {
        this.blackjackHandsSimulation = new BlackjackHandsSimulation();
        this.addChild(this.blackjackHandsSimulation);
    }

    private createAndAddBetPicker(): void {
        this.betPicker = new BetPicker();
        this.betPicker.position = this.BET_PICKER_POSITION;
        this.addChild(this.betPicker);
    }

    private createAndAddBackground(): void {
        const background: PIXI.Sprite = PIXI.Sprite.fromImage(data.images.gameBackground);
        this.addChild(background);
    }

    private createAndAddOptionsPanel(): void {
        const optionsPanel = new OptionsPanel();
        optionsPanel.position = this.OPTIONS_PANEL_POSITION;
        this.addChild(optionsPanel);
    }

    private createAndAddHowToPlay(): void {
        HowToPlay.getInstance().position = this.HOW_TO_PLAY_POSITION;
        this.addChild(HowToPlay.getInstance());
    }

    private createAndAddInfo(): void {
        Info.getInstance().position = this.HOW_TO_PLAY_POSITION;
        this.addChild(Info.getInstance());
    }
}



// WEBPACK FOOTER //
// ./src/app/Blackjack.ts